import { useEffect, useRef, type ElementType, type ReactNode } from 'react';
import { cn } from '@/lib/utils';

/**
 * Gentle fade/slide-in when an element scrolls into view.
 * Content is visible by default (no-JS, crawlers, tests); it is only hidden once we know we can reveal it,
 * and never when the user prefers reduced motion.
 */
export function Reveal({ as: Tag = 'div', className, children, delay = 0 }: { as?: ElementType; className?: string; children: ReactNode; delay?: number }) {
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') return;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
    // Already on screen at mount: leave it visible (no flash).
    if (el.getBoundingClientRect().top < window.innerHeight * 0.9) return;

    el.dataset.reveal = 'hidden';
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          el.style.transitionDelay = `${delay}ms`;
          delete el.dataset.reveal;
          io.disconnect();
        }
      },
      // The observed area extends infinitely upwards: anything above the viewport's lower edge counts as
      // "seen". Fast smooth-scroll/anchor jumps can carry an element from below to above the viewport between
      // two frames; with this margin that still registers as a change, so nothing is left hidden.
      { rootMargin: '1000000px 0px -8% 0px' },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [delay]);

  return (
    <Tag ref={ref} className={cn('reveal', className)}>
      {children}
    </Tag>
  );
}
